import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, WifiOff, X } from "lucide-react";

/**
 * Combines two PWA UX pieces:
 *  - A compact "Instalar app" button in the sidebar footer when the browser
 *    fires `beforeinstallprompt` (user can install as a desktop app).
 *  - A top banner that shows when the device goes offline, so users know
 *    data they're seeing may be cached.
 */
export default function PwaBanners({ variant = "sidebar" }) {
  const [installEvt, setInstallEvt] = useState(null);
  const [installed, setInstalled] = useState(
    typeof window !== "undefined" &&
      (window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true)
  );
  const [offline, setOffline] = useState(
    typeof navigator !== "undefined" && navigator.onLine === false
  );

  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setInstallEvt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvt(null);
    };
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const promptInstall = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    const { outcome } = await installEvt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setInstallEvt(null);
  };

  if (variant === "offline-banner") {
    if (!offline) return null;
    return (
      <div className="bg-amber-500 text-slate-900 text-xs font-medium px-4 py-2 flex items-center justify-center gap-2" data-testid="offline-banner">
        <WifiOff className="w-4 h-4" /> Você está offline — mostrando dados em cache
      </div>
    );
  }

  // sidebar variant
  if (installed || !installEvt) return null;
  return (
    <div className="mb-3 p-3 rounded-lg bg-blue-600/15 border border-blue-500/30 text-xs">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-semibold text-white">Instalar aplicativo</div>
          <div className="text-slate-300 text-[11px] mt-0.5">
            Abra como um app de desktop, com atalho e acesso offline.
          </div>
        </div>
        <button
          onClick={() => setInstallEvt(null)}
          data-testid="dismiss-install-btn"
          className="text-slate-400 hover:text-white -mr-1 -mt-0.5"
          aria-label="Dispensar"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <Button
        size="sm"
        onClick={promptInstall}
        data-testid="install-pwa-btn"
        className="w-full h-8 bg-blue-600 hover:bg-blue-700"
      >
        <Download className="w-3.5 h-3.5 mr-1.5" /> Instalar
      </Button>
    </div>
  );
}
